import { Component, OnDestroy, AfterViewInit, ViewChild, ElementRef, ChangeDetectorRef, NgZone } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { first } from 'rxjs';
import { createWorker, Worker } from 'tesseract.js';

import { State } from '@completion/reducers';
import { getTagProjections } from '@completion/selectors';
import { SearchTags } from '@completion/actions';

import { CameraService } from './camera.service';
import { TagPlateDetectorService, PlateDetection } from './tag-plate-detector.service';
import { TagPlateAnnotationService } from './tag-plate-annotation.service';

// ═══════════════════════════════════════════════════════════════════════════
// INTERFACES
// ═══════════════════════════════════════════════════════════════════════════

export interface DetectedLine {
  text: string;
  bbox: { x0: number; y0: number; x1: number; y1: number };
  selected: boolean;
}

/** A free quadrilateral — 4 independently movable corners. */
export interface DrawnQuad {
  tl: { x: number; y: number };
  tr: { x: number; y: number };
  br: { x: number; y: number };
  bl: { x: number; y: number };
}

export interface FocusRegion {
  bbox: { x0: number; y0: number; x1: number; y1: number };
  /** Original quad if drawn manually — used for perspective warp. */
  quad?: DrawnQuad;
  color: string;
  label: string;
  confidence?: number;
}

/**
 * UI mode state machine:
 *
 *   idle ──► detecting ──► ocr-running ──► results
 *                │                             ▲
 *                └──► manual-draw ─────────────┘
 *                          │
 *                          └──► annotate (training submission)
 */
type ScanMode = 'idle' | 'detecting' | 'manual-draw' | 'annotate' | 'ocr-running' | 'results';

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'app-camera-scanner-overlay',
  templateUrl: './camera-scanner-overlay.component.html',
  styleUrls: ['./camera-scanner-overlay.component.scss'],
  standalone: false,
})
export class CameraScannerOverlayComponent implements AfterViewInit, OnDestroy {
  // ── View children ─────────────────────────────────────────────────────
  @ViewChild('video') videoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvas') canvasRef!: ElementRef<HTMLCanvasElement>;
  /** Transparent overlay canvas for bounding-box drawing & live detections. */
  @ViewChild('overlayCanvas') overlayRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;

  // ── Public state (template-bound) ─────────────────────────────────────
  detectedLines: DetectedLine[] = [];
  focusRegions: FocusRegion[] = [];
  frozen = false;
  scanning = false;
  scanStatus = '';
  error: string | null = null;
  mode: ScanMode = 'idle';

  showDebug = false;
  debugImages: { label: string; url: string }[] = [];

  /** Boxes the user has drawn manually. */
  drawnBoxes: DrawnQuad[] = [];

  /** Manual tag number input (fallback when OCR fails). */
  manualTagNumber = '';

  /** AI model status (shown in the header badge). */
  modelAvailable = false;
  modelLoading = false;

  /** Live detection FPS (configurable, default 4). */
  liveFps = 4;
  /** Minimum confidence to accept an AI detection. */
  confidenceThreshold = 0.35;
  /** Live detections drawn on the overlay while not frozen. */
  liveDetections: PlateDetection[] = [];

  // ── Private state ─────────────────────────────────────────────────────
  private fileMode = false;
  private loadedImage: HTMLImageElement | null = null;
  /** Clean copy of the frozen frame — never has overlays drawn on it. */
  private cleanCanvas: HTMLCanvasElement | null = null;
  private worker: Worker | null = null;

  /** Handle for the live-detection requestAnimationFrame loop. */
  private liveLoopId: number | null = null;
  private lastDetectTime = 0;

  /** In-progress manual drawing coordinates. */
  private drawing = false;
  private drawStart = { x: 0, y: 0 };
  private drawCurrent = { x: 0, y: 0 };

  /**
   * Corner-drag state.
   * corner index: 0=TL, 1=TR, 2=BR, 3=BL
   */
  private draggingCorner: { boxIndex: number; corner: 0 | 1 | 2 | 3 } | null = null;

  /** Whole-box drag state. */
  private draggingBox: { boxIndex: number; startPx: number; startPy: number; origQuad: DrawnQuad } | null = null;

  constructor(
    private readonly dialogRef: MatDialogRef<CameraScannerOverlayComponent>,
    private readonly cdr: ChangeDetectorRef,
    private readonly zone: NgZone,
    private readonly store: Store<State>,
    private readonly camera: CameraService,
    readonly detector: TagPlateDetectorService,
    private readonly annotationSvc: TagPlateAnnotationService,
  ) {}

  // ═══════════════════════════════════════════════════════════════════════════
  // LIFECYCLE
  // ═══════════════════════════════════════════════════════════════════════════

  async ngAfterViewInit() {
    // Start loading the YOLO model in the background (non-blocking)
    this.modelLoading = true;
    this.cdr.detectChanges();
    this.detector.loadModel().then((ok) => {
      this.modelAvailable = ok;
      this.modelLoading = false;
      this.cdr.detectChanges();
      // If model loaded and camera is active, start live detection loop
      if (ok && !this.frozen) {
        this.startLiveDetectionLoop();
      }
    });

    try {
      const stream = await this.camera.start();
      const video = this.videoRef.nativeElement;
      video.srcObject = stream;
      await new Promise<void>((resolve) => {
        video.addEventListener(
          'loadedmetadata',
          () => {
            this.canvasRef.nativeElement.width = video.videoWidth;
            this.canvasRef.nativeElement.height = video.videoHeight;
            this.syncOverlaySize();
            resolve();
          },
          { once: true },
        );
        video.play().catch(() => resolve());
      });
    } catch {
      this.error = 'Camera access denied or unavailable. You can still load an image file.';
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy() {
    this.camera.stop();
    this.worker?.terminate();
    this.stopLiveDetectionLoop();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // LIVE DETECTION LOOP
  //
  // Runs outside Angular zone via requestAnimationFrame at `liveFps`.
  // Draws bounding boxes on a transparent overlay canvas so the video
  // element is never paused (smooth UX).
  // ═══════════════════════════════════════════════════════════════════════════

  private startLiveDetectionLoop() {
    if (this.liveLoopId !== null) return;
    if (!this.detector.isModelAvailable) return;

    this.zone.runOutsideAngular(() => {
      const loop = async () => {
        if (this.frozen) {
          this.liveLoopId = null;
          return;
        }

        const now = performance.now();
        const interval = 1000 / this.liveFps;

        if (now - this.lastDetectTime >= interval) {
          this.lastDetectTime = now;
          await this.detectLiveFrame();
        }

        this.liveLoopId = requestAnimationFrame(loop);
      };
      this.liveLoopId = requestAnimationFrame(loop);
    });
  }

  private stopLiveDetectionLoop() {
    if (this.liveLoopId !== null) {
      cancelAnimationFrame(this.liveLoopId);
      this.liveLoopId = null;
    }
  }

  /**
   * Grab a frame from the video, downscale it for speed, run YOLO,
   * then paint the results on the overlay canvas.
   */
  private async detectLiveFrame() {
    const video = this.videoRef?.nativeElement;
    if (!video || video.readyState < 2) return;

    // Downscale for inference speed (max 640 on longest side)
    const MAX = 640;
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const scale = Math.min(1, MAX / Math.max(vw, vh));
    const sw = Math.round(vw * scale);
    const sh = Math.round(vh * scale);

    const offscreen = document.createElement('canvas');
    offscreen.width = sw;
    offscreen.height = sh;
    offscreen.getContext('2d')!.drawImage(video, 0, 0, sw, sh);

    try {
      const dets = await this.detector.detect(offscreen);

      // Map detections back to original video resolution
      const invScale = 1 / scale;
      this.liveDetections = dets.map((d) => ({
        bbox: {
          x0: Math.round(d.bbox.x0 * invScale),
          y0: Math.round(d.bbox.y0 * invScale),
          x1: Math.round(d.bbox.x1 * invScale),
          y1: Math.round(d.bbox.y1 * invScale),
        },
        confidence: d.confidence,
      }));

      this.drawLiveOverlay();
    } catch (e) {
      console.warn('[LiveDetect] frame failed', e);
    }
  }

  /** Paint live detections on the transparent overlay canvas. */
  private drawLiveOverlay() {
    const overlay = this.overlayRef?.nativeElement;
    if (!overlay) return;
    const ctx = overlay.getContext('2d')!;
    ctx.clearRect(0, 0, overlay.width, overlay.height);

    for (const det of this.liveDetections) {
      const { x0, y0, x1, y1 } = det.bbox;
      const w = x1 - x0;
      const h = y1 - y0;

      // Box
      ctx.strokeStyle = det.confidence >= this.confidenceThreshold ? '#0aab7d' : '#ff9800';
      ctx.lineWidth = 3;
      ctx.setLineDash([]);
      ctx.strokeRect(x0, y0, w, h);

      // Confidence badge
      ctx.fillStyle = ctx.strokeStyle;
      const label = `${Math.round(det.confidence * 100)}%`;
      ctx.font = 'bold 14px sans-serif';
      const tw = ctx.measureText(label).width + 8;
      ctx.fillRect(x0, y0 - 22, tw, 20);
      ctx.fillStyle = '#000';
      ctx.fillText(label, x0 + 4, y0 - 6);
    }
  }

  /**
   * Keep the overlay canvas pixel dimensions in sync with the source canvas.
   *
   * IMPORTANT: the overlay must match the canvas exactly — not the video element.
   * In file mode, videoWidth is 0 so reading from the video gives a wrong
   * 1280×720 fallback while the image canvas can be any size. When dimensions
   * differ, pointer coordinates (overlay space) no longer match image pixel
   * space, causing the crop to cut the wrong region.
   */
  private syncOverlaySize() {
    const canvas = this.canvasRef?.nativeElement;
    const overlay = this.overlayRef?.nativeElement;
    if (!canvas || !overlay) return;

    // For the live feed the canvas is sized to video dimensions in ngAfterViewInit;
    // for file mode it is sized to img.naturalWidth × img.naturalHeight.
    // Either way, reading from canvasRef is always correct.
    const w = canvas.width || this.videoRef?.nativeElement?.videoWidth || 1280;
    const h = canvas.height || this.videoRef?.nativeElement?.videoHeight || 720;

    overlay.width = w;
    overlay.height = h;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // FILE-BASED IMAGE LOADING (for testing / debugging)
  // ═══════════════════════════════════════════════════════════════════════════

  openFilePicker() {
    this.fileInputRef?.nativeElement?.click();
  }

  async onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const img = new Image();
    const url = URL.createObjectURL(file);
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = url;
    });

    this.camera.stop();
    this.stopLiveDetectionLoop();
    this.videoRef.nativeElement.pause();

    const canvas = this.canvasRef.nativeElement;
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    canvas.getContext('2d')!.drawImage(img, 0, 0);

    this.loadedImage = img;
    this.fileMode = true;
    this.frozen = true;
    this.error = null;
    this.resetDetectionState();
    this.saveCleanCanvas(canvas);
    this.syncOverlaySize();

    URL.revokeObjectURL(url);
    this.cdr.detectChanges();

    // Run AI detection → falls back to manual draw
    await this.runDetection(canvas);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SCAN (camera capture)
  // ═══════════════════════════════════════════════════════════════════════════

  async scan() {
    if (this.scanning) return;

    this.stopLiveDetectionLoop();
    const video = this.videoRef.nativeElement;
    const canvas = this.canvasRef.nativeElement;
    const ctx = canvas.getContext('2d')!;

    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    video.pause();
    this.frozen = true;
    this.resetDetectionState();
    this.saveCleanCanvas(canvas);
    this.syncOverlaySize();
    this.cdr.detectChanges();

    await this.runDetection(canvas);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // DETECTION PIPELINE
  //
  // 1. Try YOLO model → if plates found above threshold, go to OCR
  // 2. If no model / no confident detections → enter manual-draw mode
  // ═══════════════════════════════════════════════════════════════════════════

  private async runDetection(canvas: HTMLCanvasElement) {
    this.mode = 'detecting';
    this.scanStatus = 'Detecting tag plates…';
    this.scanning = true;
    this.cdr.detectChanges();

    try {
      if (this.detector.isModelAvailable) {
        const detections = await this.detector.detect(canvas);
        const confident = detections.filter((d) => d.confidence >= this.confidenceThreshold);
        console.log(`[CameraSearch] YOLO: ${detections.length} raw, ${confident.length} above threshold`);

        if (confident.length > 0) {
          this.focusRegions = confident.map((d, i) => ({
            bbox: d.bbox,
            color: '#0aab7d',
            label: `Plate ${i + 1} (${Math.round(d.confidence * 100)}%)`,
            confidence: d.confidence,
          }));
          this.drawFrozenOverlay();
          this.cdr.detectChanges();

          await this.runOcrOnRegions(canvas);
          return;
        }
      }

      // No model or low confidence → manual drawing
      this.enterManualDrawMode();
    } catch (e) {
      console.error('[CameraSearch] Detection failed', e);
      this.enterManualDrawMode();
    }
  }

  /** Submit detection+OCR result to backend. Only called after successful OCR. */
  private submitDetectionResult(canvas: HTMLCanvasElement, recognizedTexts: string[]) {
    const base64 = this.annotationSvc.canvasToBase64(this.cleanCanvas ?? canvas);
    const boxes = this.annotationSvc.normalizeBoxes(
      this.focusRegions.map((r) => r.bbox),
      canvas.width,
      canvas.height,
    );
    this.annotationSvc
      .submit({
        image: base64,
        detections: boxes.map((b, i) => {
          const region = this.focusRegions[i];
          // Always send corners for training — derive from bbox when no explicit quad exists
          const quad = region?.quad ?? {
            tl: { x: region.bbox.x0, y: region.bbox.y0 },
            tr: { x: region.bbox.x1, y: region.bbox.y0 },
            br: { x: region.bbox.x1, y: region.bbox.y1 },
            bl: { x: region.bbox.x0, y: region.bbox.y1 },
          };
          return {
            bbox: b,
            confidence: region?.confidence ?? 1,
            manualText: recognizedTexts[i],
            quad: this.annotationSvc.normalizeQuad(quad, canvas.width, canvas.height),
          };
        }),
        modelVersion: this.detector.modelVersion || 'manual',
        timestamp: new Date().toISOString(),
      })
      .subscribe();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // MANUAL RECTANGLE DRAWING
  //
  // Active in 'manual-draw' and 'annotate' modes.
  // Uses the overlay canvas so the frozen source image stays clean.
  // ═══════════════════════════════════════════════════════════════════════════

  private enterManualDrawMode() {
    this.mode = 'manual-draw';
    this.scanning = false;
    this.scanStatus = '';
    this.drawFrozenOverlay();
    this.cdr.detectChanges();
  }

  onOverlayPointerDown(event: PointerEvent) {
    if (this.mode !== 'manual-draw' && this.mode !== 'annotate') return;

    const { px, py } = this.pointerToCanvas(event);
    (event.target as Element).setPointerCapture(event.pointerId);

    // ── 1. Check corner handles first (highest priority) ──────────────
    const corner = this.hitTestCorner(px, py);
    if (corner) {
      this.draggingCorner = corner;
      return;
    }

    // ── 2. Check if inside an existing box (drag whole box) ───────────
    const boxIdx = this.hitTestBox(px, py);
    if (boxIdx >= 0) {
      const b = this.drawnBoxes[boxIdx];
      this.draggingBox = {
        boxIndex: boxIdx,
        startPx: px,
        startPy: py,
        origQuad: {
          tl: { ...b.tl },
          tr: { ...b.tr },
          br: { ...b.br },
          bl: { ...b.bl },
        },
      };
      return;
    }

    // ── 3. Start drawing a new box ────────────────────────────────────
    this.drawing = true;
    this.drawStart = { x: px, y: py };
    this.drawCurrent = { x: px, y: py };
  }

  onOverlayPointerMove(event: PointerEvent) {
    const { px, py } = this.pointerToCanvas(event);
    const overlay = this.overlayRef.nativeElement;
    const cpx = Math.max(0, Math.min(overlay.width, px));
    const cpy = Math.max(0, Math.min(overlay.height, py));

    // ── Corner drag ──────────────────────────────────────────────────
    if (this.draggingCorner) {
      const { boxIndex, corner } = this.draggingCorner;
      const b = this.drawnBoxes[boxIndex];
      switch (corner) {
        case 0:
          b.tl.x = cpx;
          b.tl.y = cpy;
          break; // TL
        case 1:
          b.tr.x = cpx;
          b.tr.y = cpy;
          break; // TR
        case 2:
          b.br.x = cpx;
          b.br.y = cpy;
          break; // BR
        case 3:
          b.bl.x = cpx;
          b.bl.y = cpy;
          break; // BL
      }
      this.drawFrozenOverlay();
      return;
    }

    // ── Whole-box drag ───────────────────────────────────────────────
    if (this.draggingBox) {
      const { startPx, startPy, origQuad, boxIndex } = this.draggingBox;
      const dx = cpx - startPx;
      const dy = cpy - startPy;
      const b = this.drawnBoxes[boxIndex];
      b.tl.x = origQuad.tl.x + dx;
      b.tl.y = origQuad.tl.y + dy;
      b.tr.x = origQuad.tr.x + dx;
      b.tr.y = origQuad.tr.y + dy;
      b.br.x = origQuad.br.x + dx;
      b.br.y = origQuad.br.y + dy;
      b.bl.x = origQuad.bl.x + dx;
      b.bl.y = origQuad.bl.y + dy;
      this.drawFrozenOverlay();
      return;
    }

    // ── New-box drawing ───────────────────────────────────────────────
    if (!this.drawing) {
      // Update cursor based on hover
      this.updateCursor(px, py);
      return;
    }
    this.drawCurrent = { x: cpx, y: cpy };
    this.drawFrozenOverlay();
  }

  onOverlayPointerUp() {
    // ── Finalise corner drag ─────────────────────────────────────────
    if (this.draggingCorner) {
      // No normalisation — free quad corners can be anywhere
      this.draggingCorner = null;
      this.drawFrozenOverlay();
      this.cdr.detectChanges();
      return;
    }

    // ── Finalise box drag ─────────────────────────────────────────────
    if (this.draggingBox) {
      this.draggingBox = null;
      this.drawFrozenOverlay();
      this.cdr.detectChanges();
      return;
    }

    // ── Finalise new box ──────────────────────────────────────────────
    if (!this.drawing) return;
    this.drawing = false;

    const x0 = Math.round(Math.min(this.drawStart.x, this.drawCurrent.x));
    const y0 = Math.round(Math.min(this.drawStart.y, this.drawCurrent.y));
    const x1 = Math.round(Math.max(this.drawStart.x, this.drawCurrent.x));
    const y1 = Math.round(Math.max(this.drawStart.y, this.drawCurrent.y));

    if (x1 - x0 > 20 && y1 - y0 > 10) {
      this.drawnBoxes.push({
        tl: { x: x0, y: y0 },
        tr: { x: x1, y: y0 },
        br: { x: x1, y: y1 },
        bl: { x: x0, y: y1 },
      });
    }
    this.drawFrozenOverlay();
    this.cdr.detectChanges();
  }

  // ── Hit-test helpers ─────────────────────────────────────────────────────

  /**
   * Return the box index + corner (0=TL,1=TR,2=BR,3=BL) if the pointer
   * is within HANDLE_HIT_RADIUS of a corner handle, else null.
   * Iterates in reverse so the top-most (last-drawn) box wins.
   */
  private hitTestCorner(px: number, py: number): { boxIndex: number; corner: 0 | 1 | 2 | 3 } | null {
    // Use the larger of the visual radius or ~20 screen-pixels (converted to canvas space)
    const R = Math.max(14, this.screenToCanvasRadius(20));
    for (let i = this.drawnBoxes.length - 1; i >= 0; i--) {
      const b = this.drawnBoxes[i];
      const corners: [number, number, 0 | 1 | 2 | 3][] = [
        [b.tl.x, b.tl.y, 0],
        [b.tr.x, b.tr.y, 1],
        [b.br.x, b.br.y, 2],
        [b.bl.x, b.bl.y, 3],
      ];
      for (const [cx, cy, c] of corners) {
        const dx = px - cx;
        const dy = py - cy;
        if (dx * dx + dy * dy <= R * R) {
          return { boxIndex: i, corner: c };
        }
      }
    }
    return null;
  }

  /** Return the index of the topmost quad whose center handle the pointer hits, or -1. */
  private hitTestBox(px: number, py: number): number {
    const R = Math.max(16, this.screenToCanvasRadius(22));
    for (let i = this.drawnBoxes.length - 1; i >= 0; i--) {
      const b = this.drawnBoxes[i];
      const cx = (b.tl.x + b.tr.x + b.br.x + b.bl.x) / 4;
      const cy = (b.tl.y + b.tr.y + b.br.y + b.bl.y) / 4;
      const dx = px - cx;
      const dy = py - cy;
      if (dx * dx + dy * dy <= R * R) return i;
    }
    return -1;
  }

  /**
   * Point-in-convex-quad test using cross products.
   * Works for any convex quadrilateral (including perspective-skewed ones).
   */
  private pointInQuad(px: number, py: number, q: DrawnQuad): boolean {
    const pts = [q.tl, q.tr, q.br, q.bl];
    for (let i = 0; i < 4; i++) {
      const a = pts[i];
      const b = pts[(i + 1) % 4];
      // Cross product of edge AB × AP — must be >= 0 for all edges (CCW winding)
      if ((b.x - a.x) * (py - a.y) - (b.y - a.y) * (px - a.x) < 0) return false;
    }
    return true;
  }

  /**
   * Convert a PointerEvent to canvas pixel coordinates.
   *
   * Accounts for `object-fit: contain` on the overlay canvas:
   * the actual rendered content may be letterboxed/pillarboxed inside
   * the element's CSS box, so we must calculate the real content rect.
   */
  private pointerToCanvas(event: PointerEvent): { px: number; py: number } {
    const { contentLeft, contentTop, scaleX, scaleY } = this.getCanvasContentRect();
    return {
      px: (event.clientX - contentLeft) * scaleX,
      py: (event.clientY - contentTop) * scaleY,
    };
  }

  /** Convert a screen-pixel radius to canvas-pixel radius. */
  private screenToCanvasRadius(screenPx: number): number {
    const { scaleX, scaleY } = this.getCanvasContentRect();
    // Use the average of X and Y scale so circles stay roughly round
    return (screenPx * (scaleX + scaleY)) / 2;
  }

  /**
   * Calculate the actual rendered content area of the overlay canvas,
   * accounting for `object-fit: contain` letterboxing / pillarboxing.
   *
   * Returns the screen-space origin of the content and the
   * screen→canvas scale factors for each axis.
   */
  private getCanvasContentRect(): {
    contentLeft: number;
    contentTop: number;
    contentWidth: number;
    contentHeight: number;
    scaleX: number;
    scaleY: number;
  } {
    const overlay = this.overlayRef?.nativeElement;
    if (!overlay) {
      return { contentLeft: 0, contentTop: 0, contentWidth: 1, contentHeight: 1, scaleX: 1, scaleY: 1 };
    }

    const rect = overlay.getBoundingClientRect();
    const cw = overlay.width; // internal canvas resolution
    const ch = overlay.height;

    // object-fit: contain → scale to fit, preserve aspect ratio
    const canvasAspect = cw / ch;
    const elemAspect = rect.width / rect.height;

    let contentWidth: number;
    let contentHeight: number;

    if (canvasAspect > elemAspect) {
      // Canvas is wider than element → pillarboxed (bars top/bottom)
      contentWidth = rect.width;
      contentHeight = rect.width / canvasAspect;
    } else {
      // Canvas is taller than element → letterboxed (bars left/right)
      contentHeight = rect.height;
      contentWidth = rect.height * canvasAspect;
    }

    const contentLeft = rect.left + (rect.width - contentWidth) / 2;
    const contentTop = rect.top + (rect.height - contentHeight) / 2;

    return {
      contentLeft,
      contentTop,
      contentWidth,
      contentHeight,
      scaleX: cw / contentWidth,
      scaleY: ch / contentHeight,
    };
  }

  /** Set the CSS cursor based on what the pointer is hovering over. */
  private updateCursor(px: number, py: number) {
    const overlay = this.overlayRef?.nativeElement;
    if (!overlay) return;
    if (this.hitTestCorner(px, py)) {
      overlay.style.cursor = 'grab';
    } else if (this.hitTestBox(px, py) >= 0) {
      overlay.style.cursor = 'move';
    } else {
      overlay.style.cursor = 'crosshair';
    }
  }

  clearDrawnBoxes() {
    this.drawnBoxes = [];
    this.drawFrozenOverlay();
    this.cdr.detectChanges();
  }

  undoLastBox() {
    this.drawnBoxes.pop();
    this.drawFrozenOverlay();
    this.cdr.detectChanges();
  }

  /** Confirm manually-drawn boxes and proceed to OCR. */
  async confirmManualBoxes() {
    if (this.drawnBoxes.length === 0) return;

    this.focusRegions = this.drawnBoxes.map((q, i) => ({
      bbox: this.quadBoundingBox(q),
      quad: { tl: { ...q.tl }, tr: { ...q.tr }, br: { ...q.br }, bl: { ...q.bl } },
      color: '#42A5F5',
      label: `Manual region ${i + 1}`,
    }));

    this.drawFrozenOverlay();
    await this.runOcrOnRegions(this.canvasRef.nativeElement);
  }

  /** Axis-aligned bounding box that contains the quad (used for focus chips). */
  private quadBoundingBox(q: DrawnQuad): { x0: number; y0: number; x1: number; y1: number } {
    const xs = [q.tl.x, q.tr.x, q.br.x, q.bl.x];
    const ys = [q.tl.y, q.tr.y, q.br.y, q.bl.y];
    return { x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys) };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ANNOTATION MODE (training data collection)
  // ═══════════════════════════════════════════════════════════════════════════

  enterAnnotateMode() {
    this.mode = 'annotate';
    this.drawnBoxes = [];
    this.detectedLines = [];
    this.focusRegions = [];
    this.drawFrozenOverlay();
    this.cdr.detectChanges();
  }

  /**
   * Enter annotate mode with the current focus regions pre-loaded as
   * editable drawn boxes. This lets the user adjust AI-detected (or
   * manual) regions that didn't produce valid OCR results and submit
   * them as corrected training samples.
   */
  refineAndTrain() {
    // Convert existing focusRegions → editable DrawnQuads
    this.drawnBoxes = this.focusRegions.map((r) => {
      if (r.quad) {
        return { tl: { ...r.quad.tl }, tr: { ...r.quad.tr }, br: { ...r.quad.br }, bl: { ...r.quad.bl } };
      }
      const { x0, y0, x1, y1 } = r.bbox;
      return { tl: { x: x0, y: y0 }, tr: { x: x1, y: y0 }, br: { x: x1, y: y1 }, bl: { x: x0, y: y1 } };
    });
    this.detectedLines = [];
    this.focusRegions = [];
    this.mode = 'annotate';
    this.drawFrozenOverlay();
    this.cdr.detectChanges();
  }

  async submitAnnotations() {
    if (this.drawnBoxes.length === 0 || !this.cleanCanvas) return;
    // Go straight to OCR — the result (with recognized text) is submitted
    // to the backend only after OCR succeeds, in submitDetectionResult().
    await this.confirmManualBoxes();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // OCR PIPELINE
  // ═══════════════════════════════════════════════════════════════════════════

  private async runOcrOnRegions(canvas: HTMLCanvasElement) {
    this.mode = 'ocr-running';
    this.scanning = true;
    this.scanStatus = `Recognising text in ${this.focusRegions.length} region(s)…`;
    this.cdr.detectChanges();

    const cleanSrc = this.cleanCanvas ?? canvas;

    try {
      this.worker = await createWorker('eng', 1, {
        logger: (m: any) => console.log('[tesseract]', m),
      });
      await this.worker.setParameters({
        tessedit_pageseg_mode: '3',
        preserve_interword_spaces: '1',
        user_defined_dpi: '300',
      } as any);

      const SCALE = 3;
      const allLines: DetectedLine[] = [];

      for (let ri = 0; ri < this.focusRegions.length; ri++) {
        const region = this.focusRegions[ri];
        // Crop: if a quad was drawn → perspective-correct warp, else axis-aligned crop
        let cropCanvas: HTMLCanvasElement;
        let cx0 = 0,
          cy0 = 0;

        if (region.quad) {
          // Check if the quad is actually axis-aligned (simple rectangle) —
          // if so, use a fast drawImage crop instead of the full perspective warp.
          const q = region.quad;
          const isAxisAligned =
            Math.abs(q.tl.y - q.tr.y) < 2 &&
            Math.abs(q.bl.y - q.br.y) < 2 &&
            Math.abs(q.tl.x - q.bl.x) < 2 &&
            Math.abs(q.tr.x - q.br.x) < 2;

          if (isAxisAligned) {
            // Simple axis-aligned crop (no perspective warp needed)
            const PAD = 16;
            cx0 = Math.max(0, Math.round(Math.min(q.tl.x, q.bl.x)) - PAD);
            cy0 = Math.max(0, Math.round(Math.min(q.tl.y, q.tr.y)) - PAD);
            const cx1 = Math.min(cleanSrc.width, Math.round(Math.max(q.tr.x, q.br.x)) + PAD);
            const cy1 = Math.min(cleanSrc.height, Math.round(Math.max(q.bl.y, q.br.y)) + PAD);
            const rw2 = cx1 - cx0;
            const rh2 = cy1 - cy0;
            if (rw2 < 10 || rh2 < 10) continue;
            cropCanvas = document.createElement('canvas');
            cropCanvas.width = rw2;
            cropCanvas.height = rh2;
            cropCanvas.getContext('2d')!.drawImage(cleanSrc, cx0, cy0, rw2, rh2, 0, 0, rw2, rh2);
            console.log(`[CameraSearch] Region ${ri + 1}: axis-aligned crop ${rw2}×${rh2} at (${cx0},${cy0})`);
          } else {
            // Perspective warp: flattens the quad to a rectangle regardless of angle
            cropCanvas = this.warpPerspective(cleanSrc, q);
            console.log(`[CameraSearch] Region ${ri + 1}: perspective warp ${cropCanvas.width}×${cropCanvas.height} from quad`, q);
          }
        } else {
          const PAD = 16;
          cx0 = Math.max(0, region.bbox.x0 - PAD);
          cy0 = Math.max(0, region.bbox.y0 - PAD);
          const cx1 = Math.min(cleanSrc.width, region.bbox.x1 + PAD);
          const cy1 = Math.min(cleanSrc.height, region.bbox.y1 + PAD);
          const rw2 = cx1 - cx0;
          const rh2 = cy1 - cy0;
          if (rw2 < 10 || rh2 < 10) continue;
          cropCanvas = document.createElement('canvas');
          cropCanvas.width = rw2;
          cropCanvas.height = rh2;
          cropCanvas.getContext('2d')!.drawImage(cleanSrc, cx0, cy0, rw2, rh2, 0, 0, rw2, rh2);
        }

        const rw = cropCanvas.width;
        const rh = cropCanvas.height;
        this.debugImages.push({ label: `Region ${ri + 1} – raw crop`, url: cropCanvas.toDataURL() });

        // Preprocessed: returns the auto-corrected image (dark-text-on-white)
        // and its inverse so we always have both polarities to try
        const { normal: procNormal, inverted: procInverted } = this.preprocessForOcr(cropCanvas);
        this.debugImages.push({ label: `Region ${ri + 1} – preprocessed`, url: procNormal.toDataURL() });
        this.debugImages.push({ label: `Region ${ri + 1} – preprocessed (inv)`, url: procInverted.toDataURL() });

        // Raw upscaled (no binarisation — useful when plate has good contrast)
        const rawUp = document.createElement('canvas');
        rawUp.width = cropCanvas.width * SCALE;
        rawUp.height = cropCanvas.height * SCALE;
        const rCtx = rawUp.getContext('2d')!;
        rCtx.imageSmoothingEnabled = true;
        rCtx.imageSmoothingQuality = 'high';
        rCtx.drawImage(cropCanvas, 0, 0, rawUp.width, rawUp.height);

        // Run OCR on all three variants — pick the one yielding the most text
        const [r0, r1, r2] = await Promise.all([
          this.worker.recognize(procNormal),
          this.worker.recognize(procInverted),
          this.worker.recognize(rawUp),
        ]);

        const texts = [((r0.data as any).text || '').trim(), ((r1.data as any).text || '').trim(), ((r2.data as any).text || '').trim()];

        // Pick whichever variant produced the longest result
        const bestIdx = texts.reduce((best, t, i) => (t.length > texts[best].length ? i : best), 0);
        const bestData = [r0.data, r1.data, r2.data][bestIdx];
        const bestText = texts[bestIdx];

        this.debugImages.push({
          label: `Region ${ri + 1} – best (variant ${bestIdx}): "${bestText.slice(0, 40)}"`,
          url: [procNormal, procInverted, rawUp][bestIdx].toDataURL(),
        });

        const usedProcessed = bestIdx < 2; // 0 or 1 = binarised, 2 = raw

        // Extract lines from the best result
        let lines: any[] = (bestData as any).lines ?? [];
        if (!lines.length) {
          lines = (bestData as any).blocks?.flatMap((b: any) => b.paragraphs?.flatMap((p: any) => p.lines ?? []) ?? []) ?? [];
        }
        if (!lines.length) {
          const full = ((bestData as any).text || '').trim();
          if (full) {
            const textLines = full.split('\n').filter((t: string) => t.trim().length > 0);
            const lineH = rh / Math.max(textLines.length, 1);
            lines = textLines.map((t: string, idx: number) => ({
              text: t,
              bbox: { x0: 0, y0: Math.round(idx * lineH * SCALE), x1: Math.round(rw * SCALE), y1: Math.round((idx + 1) * lineH * SCALE) },
            }));
          }
        }

        const BORDER = usedProcessed ? 30 : 0;
        // For warped regions, map back to the quad bounding box origin
        const originX = region.quad ? region.bbox.x0 : cx0;
        const originY = region.quad ? region.bbox.y0 : cy0;
        for (const l of lines) {
          const txt = (l.text ?? '').trim();
          if (txt.length < 1) continue;
          allLines.push({
            text: txt,
            bbox: {
              x0: Math.round((l.bbox.x0 - BORDER) / SCALE) + originX,
              y0: Math.round((l.bbox.y0 - BORDER) / SCALE) + originY,
              x1: Math.round((l.bbox.x1 - BORDER) / SCALE) + originX,
              y1: Math.round((l.bbox.y1 - BORDER) / SCALE) + originY,
            },
            selected: false,
          });
        }
      }

      this.detectedLines = this.deduplicateLines(allLines);
      this.mode = 'results';

      // Only submit to backend when OCR actually found text
      if (this.detectedLines.length > 0) {
        this.submitDetectionResult(
          canvas,
          this.detectedLines.map((l) => l.text),
        );
      }

      this.autoMatchAndSearch();
      this.drawFrozenOverlay();
    } catch (_e) {
      this.error = 'OCR recognition failed.';
    } finally {
      this.scanStatus = '';
      this.scanning = false;
      this.cdr.detectChanges();
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // UI ACTIONS
  // ═══════════════════════════════════════════════════════════════════════════

  get allSelected(): boolean {
    return this.detectedLines.length > 0 && this.detectedLines.every((l) => l.selected);
  }

  get hasSelection(): boolean {
    return this.detectedLines.some((l) => l.selected);
  }

  toggleLine(line: DetectedLine) {
    line.selected = !line.selected;
    this.drawFrozenOverlay();
    this.cdr.detectChanges();
  }

  selectAll() {
    const allSel = this.allSelected;
    this.detectedLines.forEach((l) => (l.selected = !allSel));
    this.drawFrozenOverlay();
    this.cdr.detectChanges();
  }

  confirmSelection() {
    const selected = this.detectedLines.filter((l) => l.selected).map((l) => l.text);
    this.dialogRef.close(selected);
  }

  /** Add manually typed tag number to the detected lines list. */
  addManualTag() {
    const text = this.manualTagNumber.trim();
    if (!text) return;
    this.detectedLines.push({
      text,
      bbox: { x0: 0, y0: 0, x1: 0, y1: 0 },
      selected: true,
    });
    this.manualTagNumber = '';
    this.cdr.detectChanges();
  }

  /** Search a manually entered tag number directly. */
  searchManualTag() {
    const text = this.manualTagNumber.trim();
    if (!text) return;
    this.dialogRef.close([text]);
  }

  toggleDebug() {
    this.showDebug = !this.showDebug;
    this.cdr.detectChanges();
  }

  retake() {
    this.frozen = false;
    this.fileMode = false;
    this.loadedImage = null;
    this.cleanCanvas = null;
    this.mode = 'idle';
    this.error = null;
    this.resetDetectionState();
    const canvas = this.canvasRef.nativeElement;
    canvas.getContext('2d')!.clearRect(0, 0, canvas.width, canvas.height);
    this.clearOverlay();
    this.videoRef.nativeElement.play();
    this.cdr.detectChanges();
    if (this.modelAvailable) this.startLiveDetectionLoop();
  }

  close() {
    this.dialogRef.close(null);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // TAG MATCHING
  // ═══════════════════════════════════════════════════════════════════════════

  private autoMatchAndSearch() {
    this.store
      .select(getTagProjections)
      .pipe(first())
      .subscribe((tagProjections) => {
        if (!tagProjections || tagProjections.size === 0) return;

        const normalizedMap = new Map<string, string>();
        for (const tag of tagProjections.keys()) {
          normalizedMap.set(this.normalizeForMatch(tag), tag);
        }

        const matched: string[] = [];
        for (const line of this.detectedLines) {
          let hit: string | undefined;

          // 1. Exact
          if (tagProjections.has(line.text)) hit = line.text;

          // 2. Normalized
          if (!hit) hit = normalizedMap.get(this.normalizeForMatch(line.text));

          // 3. Substring
          if (!hit) {
            const norm = this.normalizeForMatch(line.text);
            for (const [nTag, orig] of normalizedMap) {
              if (nTag.length >= 5 && norm.includes(nTag)) {
                hit = orig;
                break;
              }
            }
          }

          if (hit) {
            line.selected = true;
            if (!matched.includes(hit)) matched.push(hit);
          }
        }

        if (matched.length > 0) {
          this.store.dispatch(new SearchTags(matched));
          this.dialogRef.close(matched);
        }
      });
  }

  private normalizeForMatch(text: string): string {
    return text
      .toUpperCase()
      .replace(/[\s\-–—_.,;:|/\\]/g, '')
      .trim();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // DRAWING HELPERS
  // ═══════════════════════════════════════════════════════════════════════════

  private saveCleanCanvas(source: HTMLCanvasElement) {
    this.cleanCanvas = document.createElement('canvas');
    this.cleanCanvas.width = source.width;
    this.cleanCanvas.height = source.height;
    this.cleanCanvas.getContext('2d')!.drawImage(source, 0, 0);
  }

  /**
   * Redraw overlays on the transparent overlay canvas when the frame is frozen.
   * The source image stays on the main canvas below — never touched.
   */
  private drawFrozenOverlay() {
    const overlay = this.overlayRef?.nativeElement;
    if (!overlay) return;
    const ctx = overlay.getContext('2d')!;
    ctx.clearRect(0, 0, overlay.width, overlay.height);

    // AI / confirmed focus regions
    for (const region of this.focusRegions) {
      const { x0, y0, x1, y1 } = region.bbox;
      ctx.save();
      ctx.strokeStyle = region.color;
      ctx.lineWidth = 3;
      ctx.setLineDash([8, 4]);
      ctx.strokeRect(x0, y0, x1 - x0, y1 - y0);
      ctx.globalAlpha = 0.1;
      ctx.fillStyle = region.color;
      ctx.fillRect(x0, y0, x1 - x0, y1 - y0);
      ctx.restore();

      // Label
      ctx.save();
      ctx.font = 'bold 12px sans-serif';
      const lw = ctx.measureText(region.label).width + 10;
      ctx.fillStyle = region.color;
      ctx.fillRect(x0, y0 - 22, lw, 20);
      ctx.fillStyle = '#000';
      ctx.fillText(region.label, x0 + 5, y0 - 6);
      ctx.restore();
    }

    // User-drawn quads — clean 1px border with circular handles
    const CORNER_RADIUS = Math.max(14, this.screenToCanvasRadius(20));
    const CENTER_RADIUS = Math.max(16, this.screenToCanvasRadius(22));
    for (let i = 0; i < this.drawnBoxes.length; i++) {
      const b = this.drawnBoxes[i];
      const isActive = this.draggingCorner?.boxIndex === i || this.draggingBox?.boxIndex === i;

      const boxColor =
        this.mode === 'annotate'
          ? '#ff9100'
          : this.sampleDominantColor(
              Math.min(b.tl.x, b.bl.x),
              Math.min(b.tl.y, b.tr.y),
              Math.max(b.tr.x, b.br.x) - Math.min(b.tl.x, b.bl.x),
              Math.max(b.bl.y, b.br.y) - Math.min(b.tl.y, b.tr.y),
            );

      const pts = [b.tl, b.tr, b.br, b.bl];

      // ── 1px polygon outline ──────────────────────────────────────
      ctx.save();
      ctx.strokeStyle = isActive ? '#ffffff' : boxColor;
      ctx.lineWidth = 1;
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      pts.slice(1).forEach((p) => ctx.lineTo(p.x, p.y));
      ctx.closePath();
      ctx.stroke();
      ctx.restore();

      // ── Large circular corner handles ─────────────────────────────
      pts.forEach((pt, ci) => {
        const isActiveCorner = this.draggingCorner?.boxIndex === i && this.draggingCorner?.corner === ci;
        ctx.save();
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, CORNER_RADIUS, 0, Math.PI * 2);
        ctx.fillStyle = isActiveCorner ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.25)';
        ctx.fill();
        ctx.strokeStyle = isActiveCorner ? '#ffffff' : boxColor;
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.restore();
      });

      // ── Large circular center handle (for whole-box drag) ─────────
      const cx = (b.tl.x + b.tr.x + b.br.x + b.bl.x) / 4;
      const cy = (b.tl.y + b.tr.y + b.br.y + b.bl.y) / 4;
      const isCenterActive = this.draggingBox?.boxIndex === i;
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, CENTER_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = isCenterActive ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.25)';
      ctx.fill();
      ctx.strokeStyle = isCenterActive ? '#ffffff' : boxColor;
      ctx.lineWidth = 1;
      ctx.stroke();
      // Draw a small move icon (cross) inside the center handle
      const arm = 6;
      ctx.beginPath();
      ctx.moveTo(cx - arm, cy);
      ctx.lineTo(cx + arm, cy);
      ctx.moveTo(cx, cy - arm);
      ctx.lineTo(cx, cy + arm);
      ctx.strokeStyle = isCenterActive ? '#ffffff' : boxColor;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.restore();

      // ── Index badge (top-left, small) ─────────────────────────────
      ctx.save();
      ctx.font = '500 10px monospace';
      ctx.fillStyle = boxColor;
      ctx.fillText(`#${i + 1}`, b.tl.x + CORNER_RADIUS + 3, b.tl.y - CORNER_RADIUS + 1);
      ctx.restore();
    }

    // In-progress drawing — sample center pixel to pick contrasting color,
    // then draw a thin black shadow stroke underneath for visibility on any background
    if (this.drawing) {
      const x = Math.min(this.drawStart.x, this.drawCurrent.x);
      const y = Math.min(this.drawStart.y, this.drawCurrent.y);
      const w = Math.abs(this.drawCurrent.x - this.drawStart.x);
      const h = Math.abs(this.drawCurrent.y - this.drawStart.y);

      // Sample the average brightness of the region being drawn
      const accentColor = this.sampleDominantColor(x, y, w, h);

      // Black outline first — guarantees contrast on both light and dark backgrounds
      ctx.save();
      ctx.strokeStyle = 'rgba(0,0,0,0.7)';
      ctx.lineWidth = 3;
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(x, y, w, h);
      ctx.restore();

      // Colored accent on top
      ctx.save();
      ctx.strokeStyle = accentColor;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 4]);
      ctx.shadowColor = accentColor;
      ctx.shadowBlur = 6;
      ctx.strokeRect(x, y, w, h);
      ctx.restore();
    }

    // OCR text boxes
    for (const line of this.detectedLines) {
      const { x0, y0, x1, y1 } = line.bbox;
      ctx.strokeStyle = line.selected ? '#0aab7d' : '#ff9800';
      ctx.lineWidth = line.selected ? 3 : 2;
      ctx.setLineDash([]);
      ctx.strokeRect(x0, y0, x1 - x0, y1 - y0);
      if (line.selected) {
        ctx.fillStyle = 'rgba(10, 171, 125, 0.15)';
        ctx.fillRect(x0, y0, x1 - x0, y1 - y0);
      }
    }
  }

  private clearOverlay() {
    const overlay = this.overlayRef?.nativeElement;
    if (overlay) {
      overlay.getContext('2d')!.clearRect(0, 0, overlay.width, overlay.height);
    }
  }

  private resetDetectionState() {
    this.detectedLines = [];
    this.focusRegions = [];
    this.drawnBoxes = [];
    this.debugImages = [];
    this.showDebug = false;
    this.liveDetections = [];
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // OCR PREPROCESSING
  //
  // Upscale 3×, grayscale, Otsu binarisation, white border.
  // Simpler than the old colour-distance approach — the YOLO model now
  // handles plate localisation, so we just need clean black-on-white text.
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Preprocess a plate crop for Tesseract:
   *   1. Upscale 3×
   *   2. Grayscale using luminance
   *   3. Contrast stretch (1st/99th percentile normalisation)
   *   4. Median filter (3×3) — removes camera noise without blurring text
   *   5. Adaptive (local mean) binarisation — handles uneven lighting
   *   6. Morphological opening (erode+dilate) — removes speckle survivors
   *   7. AUTO-INVERT if image is majority dark
   *   8. Add white border Tesseract needs
   *
   * Returns TWO canvases: [normal, inverted] so the caller can try both.
   */
  private preprocessForOcr(source: HTMLCanvasElement): { normal: HTMLCanvasElement; inverted: HTMLCanvasElement } {
    const SCALE = 3;
    const w = source.width * SCALE;
    const h = source.height * SCALE;

    const up = document.createElement('canvas');
    up.width = w;
    up.height = h;
    const ctx = up.getContext('2d')!;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(source, 0, 0, w, h);

    const imgData = ctx.getImageData(0, 0, w, h);
    const px = imgData.data;
    const gray = new Uint8Array(w * h);

    // ── Step 1: Grayscale ────────────────────────────────────────────
    for (let i = 0; i < w * h; i++) {
      gray[i] = Math.round(0.299 * px[i * 4] + 0.587 * px[i * 4 + 1] + 0.114 * px[i * 4 + 2]);
    }

    // ── Step 2: Contrast stretch (min-max normalisation) ─────────────
    // Expands the used intensity range to the full 0–255 span so faint
    // text against a similarly-coloured background becomes visible.
    // Use 1st / 99th percentile instead of absolute min/max so a few
    // extreme outlier pixels (specular highlights, dead pixels) don't
    // compress the useful range.
    const hist = new Uint32Array(256);
    for (let i = 0; i < gray.length; i++) hist[gray[i]]++;
    const totalPx = gray.length;
    const lo1 = Math.round(totalPx * 0.01);
    const hi1 = Math.round(totalPx * 0.99);
    let cumLo = 0,
      gMin = 0;
    for (; gMin < 256; gMin++) {
      cumLo += hist[gMin];
      if (cumLo >= lo1) break;
    }
    let cumHi = 0,
      gMax = 255;
    for (; gMax >= 0; gMax--) {
      cumHi += hist[gMax];
      if (cumHi >= totalPx - hi1) break;
    }
    const gRange = gMax - gMin || 1;
    for (let i = 0; i < gray.length; i++) {
      gray[i] = Math.min(255, Math.max(0, Math.round(((gray[i] - gMin) / gRange) * 255)));
    }

    // ── Step 3: Median filter (3×3) ──────────────────────────────────
    // Removes salt-and-pepper camera noise without blurring text edges.
    // This is the key step that prevents noise from becoming speckles
    // after binarisation, which confuse Tesseract badly.
    const filtered = new Uint8Array(w * h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        if (y === 0 || y === h - 1 || x === 0 || x === w - 1) {
          filtered[y * w + x] = gray[y * w + x];
          continue;
        }
        // Gather 3×3 neighbourhood
        const n0 = gray[(y - 1) * w + (x - 1)];
        const n1 = gray[(y - 1) * w + x];
        const n2 = gray[(y - 1) * w + (x + 1)];
        const n3 = gray[y * w + (x - 1)];
        const n4 = gray[y * w + x];
        const n5 = gray[y * w + (x + 1)];
        const n6 = gray[(y + 1) * w + (x - 1)];
        const n7 = gray[(y + 1) * w + x];
        const n8 = gray[(y + 1) * w + (x + 1)];
        // Partial sorting network for median of 9 (only need 5th element)
        const arr = [n0, n1, n2, n3, n4, n5, n6, n7, n8];
        arr.sort((a, b) => a - b);
        filtered[y * w + x] = arr[4];
      }
    }
    // Copy filtered back into gray for the next step
    gray.set(filtered);

    // ── Step 4: Adaptive threshold (local mean) ──────────────────────
    // For each pixel, compute the mean intensity in a surrounding window.
    // If the pixel is darker than  (localMean − C)  it is foreground (0),
    // otherwise background (255).  This handles gradients, shadows, and
    // uneven lighting that destroy text under a single global threshold.
    //
    // We use an integral image for O(1) per-pixel window sums.
    const BLOCK = Math.max(15, Math.round(Math.min(w, h) / 8) | 1); // odd window
    const C_BIAS = 10; // slightly more aggressive than before — median filter already removed noise

    // Build integral image (use Float64 to avoid overflow on large canvases)
    const integral = new Float64Array((w + 1) * (h + 1));
    const iw = w + 1;
    for (let y = 0; y < h; y++) {
      let rowSum = 0;
      for (let x = 0; x < w; x++) {
        rowSum += gray[y * w + x];
        integral[(y + 1) * iw + (x + 1)] = rowSum + integral[y * iw + (x + 1)];
      }
    }

    let darkPixels = 0;
    const half = BLOCK >> 1;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const y0 = Math.max(0, y - half);
        const y1 = Math.min(h - 1, y + half);
        const x0 = Math.max(0, x - half);
        const x1 = Math.min(w - 1, x + half);
        const area = (y1 - y0 + 1) * (x1 - x0 + 1);
        const sum =
          integral[(y1 + 1) * iw + (x1 + 1)] - integral[y0 * iw + (x1 + 1)] - integral[(y1 + 1) * iw + x0] + integral[y0 * iw + x0];
        const localMean = sum / area;
        const idx = y * w + x;
        const val = gray[idx] < localMean - C_BIAS ? 0 : 255;
        if (val === 0) darkPixels++;
        px[idx * 4] = val;
        px[idx * 4 + 1] = val;
        px[idx * 4 + 2] = val;
        px[idx * 4 + 3] = 255;
      }
    }
    ctx.putImageData(imgData, 0, 0);

    // ── Step 5: Morphological opening (erode → dilate) ───────────────
    // Removes small isolated dark speckles (1–2 px noise clusters) that
    // survived binarisation. Text strokes are thick enough (after 3× upscale)
    // to survive a 1-px erosion, but noise dots vanish.
    {
      const bin = new Uint8Array(w * h);
      for (let i = 0; i < w * h; i++) bin[i] = px[i * 4]; // 0 or 255

      // Erode: pixel is dark only if ALL 4-neighbours are also dark
      const eroded = new Uint8Array(w * h);
      eroded.fill(255);
      for (let y = 1; y < h - 1; y++) {
        for (let x = 1; x < w - 1; x++) {
          const i = y * w + x;
          if (bin[i] === 0 && bin[i - 1] === 0 && bin[i + 1] === 0 && bin[i - w] === 0 && bin[i + w] === 0) {
            eroded[i] = 0;
          }
        }
      }

      // Dilate: pixel is dark if ANY 4-neighbour is dark (restores text thickness)
      const opened = new Uint8Array(w * h);
      opened.fill(255);
      for (let y = 1; y < h - 1; y++) {
        for (let x = 1; x < w - 1; x++) {
          const i = y * w + x;
          if (eroded[i] === 0 || eroded[i - 1] === 0 || eroded[i + 1] === 0 || eroded[i - w] === 0 || eroded[i + w] === 0) {
            opened[i] = 0;
          }
        }
      }

      // Write back
      for (let i = 0; i < w * h; i++) {
        px[i * 4] = px[i * 4 + 1] = px[i * 4 + 2] = opened[i];
      }
      // Recount dark pixels for the inversion decision
      darkPixels = 0;
      for (let i = 0; i < w * h; i++) {
        if (opened[i] === 0) darkPixels++;
      }
      ctx.putImageData(imgData, 0, 0);
    }

    const BORDER = 30;

    const addBorder = (src: HTMLCanvasElement, invert: boolean): HTMLCanvasElement => {
      const out = document.createElement('canvas');
      out.width = w + BORDER * 2;
      out.height = h + BORDER * 2;
      const oc = out.getContext('2d')!;
      oc.fillStyle = '#FFF';
      oc.fillRect(0, 0, out.width, out.height);

      if (invert) {
        oc.drawImage(src, BORDER, BORDER);
        const od = oc.getImageData(BORDER, BORDER, w, h);
        for (let i = 0; i < od.data.length; i += 4) {
          od.data[i] = 255 - od.data[i];
          od.data[i + 1] = 255 - od.data[i + 1];
          od.data[i + 2] = 255 - od.data[i + 2];
        }
        oc.putImageData(od, BORDER, BORDER);
      } else {
        oc.drawImage(src, BORDER, BORDER);
      }
      return out;
    };

    // If >50% of the crop is dark → the plate is dark (e.g. red/blue/green)
    // and the text is light. We must invert so Tesseract sees dark-on-white.
    const majorityDark = darkPixels / (w * h) > 0.5;

    return {
      normal: addBorder(up, majorityDark), // always dark-text-on-white
      inverted: addBorder(up, !majorityDark), // always the opposite (for comparison)
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PERSPECTIVE WARP
  //
  // Maps an arbitrary quadrilateral in the source image to a flat rectangle.
  // Uses inverse mapping (dst → src) so every output pixel is filled without
  // holes. Output size = average of the quad's opposite edge lengths, which
  // preserves approximate real-world aspect ratio.
  // ═══════════════════════════════════════════════════════════════════════════

  private warpPerspective(src: HTMLCanvasElement, q: DrawnQuad): HTMLCanvasElement {
    const dstW = Math.round((Math.hypot(q.tr.x - q.tl.x, q.tr.y - q.tl.y) + Math.hypot(q.br.x - q.bl.x, q.br.y - q.bl.y)) / 2);
    const dstH = Math.round((Math.hypot(q.bl.x - q.tl.x, q.bl.y - q.tl.y) + Math.hypot(q.br.x - q.tr.x, q.br.y - q.tr.y)) / 2);

    const dst = document.createElement('canvas');
    dst.width = Math.max(1, dstW);
    dst.height = Math.max(1, dstH);

    const srcCtx = src.getContext('2d')!;
    const srcData = srcCtx.getImageData(0, 0, src.width, src.height);
    const dstCtx = dst.getContext('2d')!;
    const dstData = dstCtx.createImageData(dstW, dstH);

    // Homography: rectangle corners → quad corners (inverse mapping dst→src)
    const H = this.computeHomography(
      [
        { x: 0, y: 0 },
        { x: dstW, y: 0 },
        { x: dstW, y: dstH },
        { x: 0, y: dstH },
      ],
      [q.tl, q.tr, q.br, q.bl],
    );

    const sw = src.width,
      sh = src.height;
    for (let dy = 0; dy < dstH; dy++) {
      for (let dx = 0; dx < dstW; dx++) {
        const ww = H[6] * dx + H[7] * dy + H[8];
        const sx = Math.round((H[0] * dx + H[1] * dy + H[2]) / ww);
        const sy = Math.round((H[3] * dx + H[4] * dy + H[5]) / ww);
        if (sx < 0 || sx >= sw || sy < 0 || sy >= sh) continue;
        const si = (sy * sw + sx) * 4;
        const di = (dy * dstW + dx) * 4;
        dstData.data[di] = srcData.data[si];
        dstData.data[di + 1] = srcData.data[si + 1];
        dstData.data[di + 2] = srcData.data[si + 2];
        dstData.data[di + 3] = 255;
      }
    }

    dstCtx.putImageData(dstData, 0, 0);
    return dst;
  }

  /**
   * Compute a 3×3 projective homography from 4 src→dst point pairs.
   * Solves the 8-equation linear system via Gaussian elimination.
   * Returns a flat array of 9 values [h0..h8] where h8 = 1.
   */
  private computeHomography(srcPts: { x: number; y: number }[], dstPts: { x: number; y: number }[]): number[] {
    const A: number[][] = [];
    for (let i = 0; i < 4; i++) {
      const { x: sx, y: sy } = srcPts[i];
      const { x: dx, y: dy } = dstPts[i];
      A.push([sx, sy, 1, 0, 0, 0, -sx * dx, -sy * dx, dx]);
      A.push([0, 0, 0, sx, sy, 1, -sx * dy, -sy * dy, dy]);
    }

    // Gaussian elimination with partial pivoting
    for (let col = 0; col < 8; col++) {
      let maxRow = col;
      for (let row = col + 1; row < 8; row++) {
        if (Math.abs(A[row][col]) > Math.abs(A[maxRow][col])) maxRow = row;
      }
      [A[col], A[maxRow]] = [A[maxRow], A[col]];
      const pivot = A[col][col];
      if (Math.abs(pivot) < 1e-10) continue;
      for (let j = col; j < 9; j++) A[col][j] /= pivot;
      for (let row = 0; row < 8; row++) {
        if (row === col) continue;
        const f = A[row][col];
        for (let j = col; j < 9; j++) A[row][j] -= f * A[col][j];
      }
    }

    const h = A.map((row) => row[8]);
    h.push(1); // h8 = 1
    return h;
  }

  /**
   * Sample a sparse grid of pixels from the given rectangle and return
   * a hi-tech accent color based on whether red dominates:
   *
   *   Red-dominant  (avg R > avg G × 1.3) → '#ff3b3b'  — warm/red plate
   *   Otherwise                           → '#00ff88'  — green (neutral/safe)
   *
   * Falls back to '#00ff88' if the source canvas is unavailable or the
   * region is too small.
   */
  private sampleDominantColor(rx: number, ry: number, rw: number, rh: number): string {
    if (rw < 4 || rh < 4) return '#00ff88';
    const src = this.cleanCanvas ?? this.canvasRef?.nativeElement;
    if (!src) return '#00ff88';

    const x0 = Math.max(0, Math.round(rx));
    const y0 = Math.max(0, Math.round(ry));
    const w = Math.min(src.width - x0, Math.round(rw));
    const h = Math.min(src.height - y0, Math.round(rh));
    if (w < 2 || h < 2) return '#00ff88';

    const SAMPLES = 15;
    const stepX = Math.max(1, Math.floor(w / SAMPLES));
    const stepY = Math.max(1, Math.floor(h / SAMPLES));
    const data = src.getContext('2d')!.getImageData(x0, y0, w, h).data;

    let sumR = 0,
      sumG = 0,
      count = 0;
    for (let py = 0; py < h; py += stepY) {
      for (let px = 0; px < w; px += stepX) {
        const idx = (py * w + px) * 4;
        sumR += data[idx];
        sumG += data[idx + 1];
        count++;
      }
    }
    if (count === 0) return '#00ff88';
    return sumR / count > (sumG / count) * 1.3 ? '#ff3b3b' : '#00ff88';
  }

  private deduplicateLines(lines: DetectedLine[]): DetectedLine[] {
    const out: DetectedLine[] = [];
    for (const line of lines) {
      const dup = out.some((e) => e.text === line.text && this.iou(e.bbox, line.bbox) > 0.3);
      if (!dup) out.push(line);
    }
    return out;
  }

  private iou(a: { x0: number; y0: number; x1: number; y1: number }, b: { x0: number; y0: number; x1: number; y1: number }): number {
    const ix0 = Math.max(a.x0, b.x0),
      iy0 = Math.max(a.y0, b.y0);
    const ix1 = Math.min(a.x1, b.x1),
      iy1 = Math.min(a.y1, b.y1);
    const inter = Math.max(0, ix1 - ix0) * Math.max(0, iy1 - iy0);
    if (inter === 0) return 0;
    const aA = (a.x1 - a.x0) * (a.y1 - a.y0);
    const aB = (b.x1 - b.x0) * (b.y1 - b.y0);
    return inter / (aA + aB - inter);
  }
}
